import React, { useState } from "react";
import { FormSection, FileUpload } from "../../../common/FormComponents";
import { Button, Paper, Typography, Chip, IconButton } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";

type EmailContent = {
  emailContent: string;
  attachments: File[];
};

type EmailContentSectionProps = {
  content: EmailContent;
  onContentChange: ({ content }: { content: string }) => void;
  onAddAttachment: ({ file }: { file: File }) => void;
  onRemoveAttachment: ({ index }: { index: number }) => void;
  errors?: Record<string, string>;
};

const EmailContentSection = ({
  content,
  onContentChange,
  onAddAttachment,
  onRemoveAttachment,
  errors = {},
}: EmailContentSectionProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onAddAttachment({ file: e.target.files[0] });
    }
  };

  // Handle file drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onAddAttachment({ file: e.dataTransfer.files[0] });
    }
  };

  // Handle drag events
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLDivElement>) => {
    const htmlContent = e.currentTarget.innerHTML;
    onContentChange({ content: htmlContent });
    setShowPlaceholder(htmlContent === "");
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  return (
    <FormSection
      title="Email Content"
      tooltipText="Create and design your email content"
    >
      <div className="mb-6">
        <div className="bg-gray-100 p-2 rounded mb-2 flex flex-wrap">
          <IconButton size="small" title="Bold">
            <FormatBoldIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Italic">
            <FormatItalicIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Underline">
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
          <div className="border-r border-gray-300 mx-1 my-1"></div>
          <IconButton size="small" title="Align Left">
            <FormatAlignLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Align Center">
            <FormatAlignCenterIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Align Right">
            <FormatAlignRightIcon fontSize="small" />
          </IconButton>
          <div className="border-r border-gray-300 mx-1 my-1"></div>
          <IconButton size="small" title="Bulleted List">
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Numbered List">
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
          <div className="border-r border-gray-300 mx-1 my-1"></div>
          <IconButton size="small" title="Insert Link">
            <InsertLinkIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Insert Image">
            <InsertPhotoIcon fontSize="small" />
          </IconButton>
        </div>

        <div
          className={`border ${
            dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
          } rounded-md p-3 min-h-[300px] relative`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {showPlaceholder && (
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none">
              <div className="text-center text-gray-500">
                <p>Drag and drop elements to build your email</p>
                <p className="text-sm">Or click to start typing</p>
              </div>
            </div>
          )}

          <div
            className="outline-none min-h-[300px] w-full"
            contentEditable
            suppressContentEditableWarning
            onInput={handleContentChange}
            dangerouslySetInnerHTML={{ __html: content.emailContent }}
          />
        </div>

        {errors.content && (
          <p className="text-red-500 text-xs mt-1">{errors.content}</p>
        )}
      </div>

      <div className="mt-6">
        <div className="flex items-center mb-2">
          <Typography variant="subtitle2">Attachments</Typography>
          <Button
            variant="text"
            startIcon={<AttachFileIcon />}
            onClick={handleAttachFile}
            size="small"
            sx={{ ml: 2 }}
          >
            Add File
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        <div className="space-y-2">
          {content.attachments.length > 0 ? (
            content.attachments.map((file, index) => (
              <Paper
                key={index}
                className="p-2 flex items-center justify-between"
                variant="outlined"
              >
                <div className="flex items-center">
                  <AttachFileIcon className="text-gray-500 mr-2" />
                  <div>
                    <Typography variant="body2" noWrap>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      {formatBytes(file.size)}
                    </Typography>
                  </div>
                </div>
                <IconButton
                  size="small"
                  onClick={() => onRemoveAttachment({ index })}
                  aria-label="remove attachment"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" className="text-gray-500">
              No attachments added
            </Typography>
          )}
        </div>

        <Typography variant="caption" className="text-gray-500 block mt-2">
          Max file size: 10MB. Supported formats: PDF, DOC, DOCX, JPG, PNG
        </Typography>
      </div>
    </FormSection>
  );
};

export default EmailContentSection;
